import { useState, useEffect, useRef } from "react";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="/"
            className="flex items-center gap-2 group cursor-pointer"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-database-2-line text-lg text-white"></i>
            </div>
            <span className="text-base md:text-lg font-semibold font-heading text-foreground-950 whitespace-nowrap">
              Sugargoo Spreadsheet
            </span>
          </a>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="/"
              className="px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line mr-1.5"></i>
              Home
            </a>

            <a
              href="/article/sugargoo-buying-guide"
              className="px-4 py-2 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-guide-line mr-1.5"></i>
              Shopping Guide
            </a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Categories</span>
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-56 bg-background-50 rounded-lg border border-background-200/70 py-1 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Sugargoo Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-white hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i
              className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}
            ></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-2 mb-4">
            <a
              href="/"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-100 hover:text-primary-700 cursor-pointer"
            >
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200">
                <i className="ri-home-line text-sm text-foreground-600"></i>
              </div>
              Home
            </a>
            <a
              href="/article/sugargoo-buying-guide"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-accent-100 hover:text-accent-700 cursor-pointer"
            >
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100">
                <i className="ri-guide-line text-sm text-accent-600"></i>
              </div>
              Shopping Guide
            </a>
          </div>

          <div className="border-t border-background-200/70 pt-3 mb-3">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 mb-2">
              Product Categories
            </p>
            <div className="grid grid-cols-1 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-700 cursor-pointer"
                >
                  <div className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <span>{cat.name}</span>
                  <i className="ri-external-link-line text-xs text-foreground-400 ml-auto"></i>
                </a>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-3 border-t border-background-200/70">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full px-4 py-2.5 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 text-sm font-medium text-center cursor-pointer whitespace-nowrap"
            >
              Sugargoo Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full px-4 py-2.5 rounded-full bg-primary-500 text-white hover:bg-primary-600 text-sm font-semibold text-center cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}